import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { ActionItemProgressDto, ActionItemWithAuditDto } from "@/types";
import { ActionItemStatus, RelatedObjectType } from "@/enums";
import {
  CreateActionItemInput,
  CreateActionItemProgressInput,
  UpdateActionItemInput,
} from "@/schemas";

export type CreateActionItemRequest = CreateActionItemInput;
export type UpdateActionItemRequest = UpdateActionItemInput;
export type CreateActionItemProgressRequest = CreateActionItemProgressInput;

// Define the type for progress with related objects
export type ActionItemProgressDtoWithRelatedObjects = ActionItemProgressDto & {
  relatedObjects?: Record<string, unknown[]>;
};

/**
 * Fetches all action items for a specific account by its ID.
 *
 * @param accountId - The ID of the account to fetch action items for
 * @returns A list of action items.
 * @throws An error if the request fails.
 */
export async function fetchActionItemsByAccountId(
  accountId: string
): Promise<ActionItemWithAuditDto[]> {
  try {
    return await apiClient<ActionItemWithAuditDto[]>(
      `/api/accounts/${accountId}/action-items`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchActionItemsByAccountId");
    throw error;
  }
}

/**
 * Fetches the top 3 upcoming action items for a specific account.
 * Orders by due date (nearest first) and limits to 3 results.
 * Only returns non-archived action items with OPEN status.
 *
 * @param accountId - The ID of the account to fetch upcoming action items for
 * @returns A list of top 3 upcoming action items.
 * @throws An error if the request fails.
 */
export async function fetchUpcomingActionItemsByAccountId(
  accountId: string
): Promise<ActionItemWithAuditDto[]> {
  try {
    return await apiClient<ActionItemWithAuditDto[]>(
      `/api/accounts/${accountId}/action-items?upcoming=true`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchUpcomingActionItemsByAccountId");
    throw error;
  }
}

/**
 * Fetches an action item by its external ID and account ID.
 *
 * @param actionItemId - The ID of the action item to fetch
 * @returns The action item data if found, null otherwise.
 * @throws An error if the request fails.
 */
export async function fetchActionItemById(
  actionItemId: string
): Promise<ActionItemWithAuditDto | null> {
  try {
    return await apiClient<ActionItemWithAuditDto>(
      `/api/action-items/${actionItemId}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchActionItemById");
    throw error;
  }
}

/**
 * Creates a new action item by sending the provided data to the backend.
 *
 * @param accountId - The ID of the account to associate the action item with.
 * @param data - The data required to create a new action item.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createActionItem(
  accountId: string,
  data: CreateActionItemRequest
): Promise<ActionItemWithAuditDto> {
  try {
    return await apiClient<ActionItemWithAuditDto>(
      `/api/accounts/${accountId}/action-items`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createActionItem");
    throw error;
  }
}

/**
 * Updates an existing action item by sending the provided data to the backend.
 *
 * @param actionItemId - The ID of the action item to update
 * @param data - The data required to update the action item
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function updateActionItem(
  actionItemId: string,
  data: UpdateActionItemRequest
): Promise<ActionItemWithAuditDto> {
  try {
    return await apiClient<ActionItemWithAuditDto>(
      `/api/action-items/${actionItemId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "updateActionItem");
    throw error;
  }
}

/**
 * Creates an action item progress update by sending the provided data to the backend.
 *
 * @param actionItemId - The ID of the action item to update progress.
 * @param data - The data required to create an action item progress update.
 * @returns A promise that resolves with the response data from the backend.
 * @throws Will throw an error if the request fails or if validation fails.
 */
export async function createActionItemProgress(
  actionItemId: string,
  data: CreateActionItemProgressRequest
): Promise<ActionItemWithAuditDto> {
  try {
    return await apiClient<ActionItemWithAuditDto>(
      `/api/action-items/${actionItemId}/progress`,
      {
        method: "POST",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "createActionItemProgress");
    throw error;
  }
}

/**
 * Fetches action item progress by action item ID
 */
export async function getActionItemProgressByActionItemId(
  actionItemId: string
): Promise<Record<string, unknown>[]> {
  try {
    return await apiClient<Record<string, unknown>[]>(
      `/api/action-items/${actionItemId}/progress`,
      {
        method: "GET",
      }
    );
  } catch (error) {
    logError(error, "getActionItemProgressByActionItemId");
    throw error;
  }
}

/**
 * Fetches action item related objects
 */
export async function fetchActionItemRelatedObjects(
  actionItemId: string,
  types: RelatedObjectType[]
): Promise<Record<string, unknown[]>> {
  try {
    const searchParams = new URLSearchParams();
    types.forEach((type) => searchParams.append("type", type));
    return await apiClient<Record<string, unknown[]>>(
      `/api/action-items/${actionItemId}/related-objects?${searchParams.toString()}`,
      {
        method: "GET",
      }
    );
  } catch (error) {
    logError(error, "fetchActionItemRelatedObjects");
    throw error;
  }
}

/**
 * Fetches action item progress, optionally including related objects.
 */
export async function fetchActionItemProgress(
  actionItemId: string,
  options?: { expand?: "relatedObjects"; relatedTypes?: string[] }
): Promise<
  ActionItemProgressDto[] | ActionItemProgressDtoWithRelatedObjects[]
> {
  try {
    const searchParams = new URLSearchParams();
    if (options?.expand === "relatedObjects") {
      searchParams.append("expand", "relatedObjects");
      if (options.relatedTypes && options.relatedTypes.length > 0) {
        searchParams.append("relatedTypes", options.relatedTypes.join(","));
      }
    }
    return await apiClient<
      ActionItemProgressDto[] | ActionItemProgressDtoWithRelatedObjects[]
    >(`/api/action-items/${actionItemId}/progress?${searchParams.toString()}`, {
      method: "GET",
    });
  } catch (error) {
    logError(error, "fetchActionItemProgress");
    throw error;
  }
}

/**
 * Deletes a action item by its ID.
 *
 * @param actionItemId - The ID of the action item to delete
 * @returns A promise that resolves when the action item is deleted.
 * @throws An error if the request fails or if the action item is not found.
 */
export async function deleteActionItem(actionItemId: string): Promise<void> {
  try {
    await apiClient<void>(`/api/action-items/${actionItemId}`, {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteActionItem");
    throw error;
  }
}

/**
 * Fetches all action items across all accounts for global dashboard statistics.
 * This aggregates action items from all accounts the user has access to.
 *
 * @returns A list of all action items across all accounts.
 * @throws An error if the request fails.
 */
export async function fetchAllActionItems(): Promise<ActionItemWithAuditDto[]> {
  try {
    return await apiClient<ActionItemWithAuditDto[]>("/api/action-items/all", {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchAllActionItems");
    throw error;
  }
}

/**
 * Fetches the current user's open action items for the active organization,
 * ordered by priority (CRITICAL first) then due date ascending.
 *
 * @returns A list of the session user's open action items.
 * @throws An error if the request fails.
 */
export async function fetchMyActionItems(): Promise<ActionItemWithAuditDto[]> {
  try {
    return await apiClient<ActionItemWithAuditDto[]>("/api/action-items/mine", {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchMyActionItems");
    throw error;
  }
}

/**
 * Fetches all open action items assigned to a specific user by their user ID.
 *
 * @param userId - The ID of the user to fetch open action items for.
 * @returns A promise that resolves with the list of open action items for the user.
 */
export async function fetchOpenActionItemsByUserId(
  userId: string
): Promise<ActionItemWithAuditDto[]> {
  try {
    return await apiClient<ActionItemWithAuditDto[]>(
      `/api/admin/users/${userId}/action-items?status=${ActionItemStatus.OPEN}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchOpenActionItemsByUserId");
    throw error;
  }
}
